// Core imports
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react"

// Table library imports
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnSort,
  OnChangeFn,
  PaginationState,
  Row,
  RowData,
  RowSelectionState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table"

// MUI imports
import {
  Box,
  Divider,
  IconButton,
  InputBaseComponentProps,
  Skeleton,
  StackProps,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Tooltip,
  Typography
} from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import { useTranslation } from "react-i18next"

// Local imports
import imageBoxIconImage from "src/assets/icons/empty-box.webp"
import { Conditional, Stack } from "src/shared/components"
import ColumnVisiblityAction from "./column-visibility"
import { getStickyColumnOffset, hasFiltersChanged } from "./data-grid-utils"
import EnhancedMuiThead from "./enhanced-mui-thead"
import PaginationControls from "./pagination-controls"

// Type declarations for table library extensions
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: "text" | "range" | "select"
    editable?: boolean
    editableInputProps?: InputBaseComponentProps
    selectOptions?: IOptions[]
    inputVariant?: "text" | "select"
    sticky?: "left" | "right"
    headerAlign?: "center" | "left" | "right"
    textAlign?: "center" | "left" | "right"
    valueType?: string
    width?: string
    maxWidth?: string
    minWidth?: string
    wrap?: boolean
    columnVisiblity?: "default" | "visible" | "hidden"
    enableClearable?: boolean
  }
}

declare module "@tanstack/table-core" {
  interface TableMeta<TData extends RowData> {
    updateData: Function
  }
}

// Props interface for DataGridMui component
interface DataGridProps<TData extends RowData, TValue> {
  id: string
  data: TData[]
  columns: ColumnDef<TData, TValue>[]
  initialFilters?: ColumnFiltersState
  onFilterChange?: (filters: any) => void
  onPaginationChange?: OnChangeFn<PaginationState>
  pageCount?: number
  isLoading?: boolean
  skeletonCount?: number
  noDataComponent?: ReactNode
  hideRowsPerPage?: boolean
  hidePagination?: boolean
  setData?: any
  onRowSelectionChange?: (ids: string[]) => void
  paginationModel?: { pageIndex?: number; pageSize?: number }
  /**
   * If true, the DataGrid will reset the page index to 0 when the data or columns change.
   * This is useful when the data is being updated and the user should be shown the first page again.
   */
  autoResetPageIndex?: boolean
  rowsPerPageOptions?: number[]
  onSortModelChange?: (sortingModel: ColumnSort) => void
  sortingModel?: SortingState[0]
  paginationMode?: "client" | "server"
  filterMode?: "client" | "server"
  sortingMode?: "client" | "server"
  initialPageSize?: number
  enableColumnFilters?: boolean
  enableSorting?: boolean
  heightNoData?: string
  height?: string
  maxHeight?: string
  style?: React.CSSProperties
  stripBorder?: boolean
  onRowClick?: (data: any) => void
  initialColumnVisibility?: any
  getRowId?: (originalRow: any, index: number, parent?: Row<any>) => string
  leftComponent?: ReactElement
  rightComponent?: ReactElement
  /**
   * When provided, a refresh button is rendered in the toolbar. The grid tracks the
   * in-flight refresh so the skeleton is not shown while refreshing (rows stay visible).
   */
  onRefresh?: () => unknown | Promise<unknown>
  hideColumnVisiblity?: boolean
  minWidth?: string
  getRowClassName?: (row: Row<any>) => string
  /** When set with onRowClick, only rows where this returns true are clickable (pointer/hover) and receive click events. */
  isRowClickable?: (data: TData) => boolean
  // columnPinning?: { left?: string[]; right?: string[] }
  paginationJustify?: StackProps["justifyContent"]
}

/**
 * DataGridMui - A customizable data grid component built with Material-UI and TanStack Table
 *
 * Features:
 * - Client/Server side pagination, filtering and sorting
 * - Column visibility control
 * - Row selection
 * - Sticky columns
 * - Custom styling options
 * - Loading states
 * - No data states
 */
const SCROLLBAR_HIDE_DELAY = 800

export function DataGridMui<TData extends RowData, TValue>({
  data,
  columns: columnsWithDefault,
  initialFilters = [],
  onFilterChange,
  onPaginationChange,
  paginationModel,
  pageCount,
  skeletonCount = 10,
  isLoading,
  id,
  hideRowsPerPage,
  hidePagination,
  noDataComponent,
  setData,
  autoResetPageIndex = false,
  paginationMode = "client",
  filterMode = "client",
  sortingMode = "client",
  onRowSelectionChange,
  onSortModelChange,
  sortingModel,
  rowsPerPageOptions,
  initialPageSize = 10,
  enableColumnFilters = true,
  enableSorting = true,
  heightNoData = "300px",
  height = "100%",
  maxHeight,
  stripBorder = true,
  getRowId,
  onRowClick,
  leftComponent,
  rightComponent,
  onRefresh,
  hideColumnVisiblity,
  getRowClassName,
  isRowClickable,
  minWidth = "100%",
  paginationJustify
}: DataGridProps<TData, TValue>) {
  const { t } = useTranslation("data-grid")
  // State management
  const [isRefreshing, setIsRefreshing] = useState(false)
  const isMountedRef = useRef(true)
  const showSkeleton = !!isLoading && !isRefreshing
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(initialFilters)
  const [sorting, setSorting] = React.useState<SortingState>(
    sortingModel ? ([sortingModel] as unknown as SortingState) : []
  )
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [anchorEl, setAnchorEl] = React.useState<any>()
  const [columnVisibility, setColumnVisibility] = React.useState<Record<string, boolean>>({})
  // Refs
  const filterRef = useRef<any>([])
  const tableRef = useRef<HTMLDivElement>(null)
  const scrollVisibilityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const ignoreScrollEventRef = useRef(false)
  const previousScrollPositionRef = useRef<{ left: number; top: number }>({ left: 0, top: 0 })

  // Scroll state
  const [isScrolledLeft, setIsScrolledLeft] = useState(false)
  const [isScrolledRight, setIsScrolledRight] = useState(false)
  const [isScrollbarVisible, setIsScrollbarVisible] = useState(false)

  // Effects for managing filters, visibility, selection and scrolling
  useEffect(() => {
    //  ------ this is for client side custom header filter when initial filter change column filter updated -------------
    if (initialFilters?.length && JSON.stringify(initialFilters) !== JSON.stringify(columnFilters)) {
      setColumnFilters(initialFilters)
    }
  }, [initialFilters])

  useEffect(() => {
    if (sortingModel && sorting.length === 0) {
      setSorting([sortingModel])
    }
  }, [sortingModel])

  useEffect(() => {
    // ----------------------- set initial column visibility visibility -------------------
    const storedVisibility = JSON.parse(localStorage.getItem(`${id}-columns`) || "{}")
    if (!!Object.keys(storedVisibility)?.length) return setColumnVisibility(storedVisibility)

    const defaultVisibility = Object.fromEntries(
      columnsWithDefault.map((col) => [col?.id ?? (col as any)?.accessorKey, col?.meta?.columnVisiblity !== "hidden"])
    )
    localStorage.setItem(`${id}-columns`, JSON.stringify(defaultVisibility))
    setColumnVisibility(defaultVisibility)
  }, [columnsWithDefault])

  //  ---------------- set column visiblity to local storage --------------------------
  useEffect(() => {
    if (!!Object.keys(columnVisibility)?.length) localStorage.setItem(`${id}-columns`, JSON.stringify(columnVisibility))
  }, [columnVisibility])

  useEffect(() => {
    if (showSkeleton) setRowSelection({})
  }, [showSkeleton])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleRefresh = useCallback(async () => {
    if (!onRefresh || isRefreshing || (isLoading && !isRefreshing)) return
    setIsRefreshing(true)
    try {
      await onRefresh()
    } finally {
      if (isMountedRef.current) setIsRefreshing(false)
    }
  }, [onRefresh, isRefreshing, isLoading])

  useEffect(() => {
    onRowSelectionChange?.(Object.keys(rowSelection))
  }, [rowSelection])

  useEffect(() => {
    onSortModelChange?.(sorting?.[0])
  }, [sorting])

  useEffect(() => {
    const handleScroll = () => {
      if (ignoreScrollEventRef.current) {
        return
      }

      if (!tableRef.current) return

      const currentLeft = tableRef.current.scrollLeft
      const currentTop = tableRef.current.scrollTop
      const horizontalDelta = Math.abs(currentLeft - previousScrollPositionRef.current.left)
      const verticalDelta = Math.abs(currentTop - previousScrollPositionRef.current.top)
      const hasHorizontalMovement = horizontalDelta > 0.5
      const hasVerticalMovement = verticalDelta > 0.5

      if (hasHorizontalMovement || hasVerticalMovement) {
        setIsScrollbarVisible(true)
        if (scrollVisibilityTimeoutRef.current) {
          clearTimeout(scrollVisibilityTimeoutRef.current)
        }

        scrollVisibilityTimeoutRef.current = setTimeout(() => {
          setIsScrollbarVisible(false)
          if (tableRef.current) {
            previousScrollPositionRef.current = {
              left: tableRef.current.scrollLeft,
              top: tableRef.current.scrollTop
            }
          }
          scrollVisibilityTimeoutRef.current = null
          ignoreScrollEventRef.current = true
          requestAnimationFrame(() => {
            ignoreScrollEventRef.current = false
          })
        }, SCROLLBAR_HIDE_DELAY)
      }

      previousScrollPositionRef.current = { left: currentLeft, top: currentTop }

      setIsScrolledLeft(currentLeft > 0)
      setIsScrolledRight(currentLeft + tableRef.current.clientWidth < tableRef.current.scrollWidth)
    }

    const tableContainer = tableRef.current
    tableContainer?.addEventListener("scroll", handleScroll)
    return () => {
      tableContainer?.removeEventListener("scroll", handleScroll)
      if (scrollVisibilityTimeoutRef.current) {
        clearTimeout(scrollVisibilityTimeoutRef.current)
        scrollVisibilityTimeoutRef.current = null
      }
    }
  }, [])

  // Memoized values and callbacks
  const columns = useMemo(() => {
    return columnsWithDefault.map((column) => {
      if (!column.enableColumnFilter && typeof column.enableColumnFilter !== "boolean") column.enableColumnFilter = true
      if (!column.enableSorting && typeof column.enableSorting !== "boolean") column.enableSorting = true
      if (!column?.meta || (!column?.meta?.minWidth && !column?.meta?.width))
        column.meta = { ...column.meta, minWidth: "auto" }
      return column
    })
  }, [columnsWithDefault])

  const onChangeColumnFilters: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      const newColumnFilters = typeof updater === "function" ? updater(columnFilters) : updater
      if (!hasFiltersChanged(newColumnFilters, columnFilters)) return
      setColumnFilters(newColumnFilters)
      onFilterChange?.(newColumnFilters)
    },
    [setColumnFilters, columnFilters, onFilterChange]
  )

  const paginationState = useMemo(() => {
    if (paginationModel?.pageIndex === undefined || paginationModel?.pageSize === undefined) return undefined

    return {
      pageIndex: paginationModel.pageIndex ?? 0,
      pageSize: paginationModel.pageSize ?? 10
    } as PaginationState
  }, [paginationModel?.pageIndex, paginationModel?.pageSize])

  // Table instance configuration
  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
      sorting,
      ...(paginationMode === "server" && paginationState && { pagination: paginationState }),
      rowSelection,
      columnVisibility
    },
    initialState: {
      pagination: {
        pageSize: initialPageSize
      }
    },
    ...(paginationMode === "server" && { rowCount: pageCount ?? data.length }),
    renderFallbackValue: "-",
    onColumnFiltersChange: onChangeColumnFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getRowId: getRowId ?? ((row: any) => row?.id),
    getCoreRowModel: getCoreRowModel(),
    ...(sortingMode === "client" && { getSortedRowModel: getSortedRowModel() }),
    ...(paginationMode === "server" && { onPaginationChange }),
    ...(filterMode === "client" && { getFilteredRowModel: getFilteredRowModel() }),
    ...(paginationMode === "client" && { getPaginationRowModel: getPaginationRowModel() }),
    manualFiltering: filterMode === "server",
    manualPagination: paginationMode === "server",
    manualSorting: sortingMode === "server",
    debugTable: true,
    debugHeaders: true,
    enableColumnFilters,
    enableSorting,
    autoResetPageIndex,
    debugColumns: false,
    meta: {
      updateData: (rowIndex: number, columnId: string, value: string) => {
        if (!setData || typeof setData !== "function") {
          throw new Error("setData must be a function")
        }

        // setData((old: ColumnDef<TData, any>[]) =>
        //   old.map((row, index) => (index === rowIndex ? { ...old[rowIndex], [columnId]: value } : row))
        // )

        const setByPath = (obj: any, path: string, nextValue: any) => {
          if (!path.includes(".")) return { ...(obj ?? {}), [path]: nextValue }

          const keys = path.split(".").filter(Boolean)
          const clone = { ...(obj ?? {}) }
          let cursor: any = clone
          for (let i = 0; i < keys.length; i++) {
            const k = keys[i]
            const isLast = i === keys.length - 1
            if (isLast) {
              cursor[k] = nextValue
            } else {
              const prev = cursor[k]
              cursor[k] = prev && typeof prev === "object" && !Array.isArray(prev) ? { ...prev } : {}
              cursor = cursor[k]
            }
          }
          return clone
        }

        setData(
          (old: TData[]) =>
            old.map((row, index) => (index === rowIndex ? (setByPath(row, columnId, value) as TData) : row)),
          rowIndex,
          columnId,
          value
        )
      }
    }
  })

  const skeletons = Array.from({ length: skeletonCount }, (_, i) => i)
  const columnCount = table.getVisibleLeafColumns().length

  return (
    <>
      <Stack
        id={id}
        mb={1}
        direction={"row"}
        justifyContent={leftComponent ? "space-between" : "flex-end"}
        alignItems={"center"}
      >
        {leftComponent}

        <Stack direction={"row"} alignItems={"center"} spacing={1} ml={"auto"}>
          <Conditional condition={!!onRefresh}>
            <Tooltip title={t("refresh")}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleRefresh}
                  disabled={!!isLoading && !isRefreshing}
                  aria-label={t("refresh")}
                >
                  <RefreshIcon
                    fontSize="small"
                    sx={{
                      animation: isRefreshing ? "data-grid-refresh-spin 0.8s linear infinite" : "none",
                      "@keyframes data-grid-refresh-spin": {
                        to: { transform: "rotate(360deg)" }
                      }
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Conditional>
          {rightComponent}
          <Divider flexItem orientation="horizontal" />
          <Conditional condition={!hideColumnVisiblity}>
            <ColumnVisiblityAction table={table} />
          </Conditional>
        </Stack>
      </Stack>
      <TableContainer
        ref={tableRef}
        className={`hovered-scrollbar2${isScrollbarVisible ? " show-scrollbar" : ""}`}
        sx={{
          height: height,
          maxHeight: maxHeight,
          border: (theme) => `1px solid ${theme.palette.table?.border}`,
          background: (theme) => theme.palette.background.default,
          borderRadius: "8px",
          overflowX: "auto"
        }}
      >
        <Table
          sx={{ minWidth: minWidth, borderCollapse: "separate" }}
          size="small"
          className={`${id}-data-grid-custom`}
        >
          <EnhancedMuiThead
            table={table}
            scrolled={{ left: isScrolledLeft ?? false, right: isScrolledRight ?? false }}
            setAnchorEl={setAnchorEl}
            anchorEl={anchorEl}
            filterRef={filterRef}
            stripBorder={stripBorder}
            id={id}
          />
          <TableBody className={`${id}-data-grid-custom-body`}>
            {!showSkeleton
              ? table.getRowModel().rows.map((row) => {
                  const rowInteractive = !!onRowClick && (isRowClickable ? isRowClickable(row.original as TData) : true)
                  return (
                    <TableRow
                      className={`${id}-data-grid-custom-row ${getRowClassName?.(row)}`}
                      onClick={() => {
                        if (!onRowClick) return
                        const original = row?.original as TData
                        if (isRowClickable && !isRowClickable(original)) return
                        onRowClick(original)
                      }}
                      sx={{
                        ...(rowInteractive && {
                          cursor: "pointer",
                          "&:hover": {
                            background: (theme) => theme.palette.table?.background,
                            "& .MuiTableCell-root": {
                              background: (theme) => theme.palette.table?.background
                            }
                          }
                        }),
                        borderBottom: (theme) => `1px solid ${theme.palette.table?.border}`,
                        "&:last-child": { borderBottom: `0px !important` }
                      }}
                      key={row.id}
                    >
                      {row.getVisibleCells().map((cell, iCell) => {
                        const stickyStyles = getCommonStickyStyles({
                          row,
                          sticky: cell.column.columnDef?.meta?.sticky,
                          index: iCell,
                          isScrolledLeft,
                          isScrolledRight
                        })

                        return (
                          <TableCell
                            key={cell.id}
                            className={`${id}-data-grid-custom-cell`}
                            align={cell.column.columnDef.meta?.textAlign}
                            sx={{
                              ...(stripBorder && {
                                borderBottom: (theme) => `1px solid ${theme.palette.table?.border}`
                              }),
                              ...stickyStyles,
                              maxWidth: cell.column.columnDef?.meta?.maxWidth || "500px"
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                display: "block",
                                ...(cell.column.columnDef?.meta?.wrap
                                  ? {
                                      whiteSpace: "normal",
                                      overflow: "visible",
                                      maxWidth: "100%"
                                    }
                                  : {
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis"
                                    })
                              }}
                              title={typeof cell.getValue() === "string" ? (cell.getValue() as string) : undefined}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Box>
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })
              : skeletons.map((skeleton) => (
                  <TableRow className={`${skeleton}-data-grid-custom-row`} key={skeleton}>
                    {Array.from({ length: columnCount }, (_, i) => i).map((cell) => (
                      <TableCell key={`${skeleton}-${cell}`}>
                        <Skeleton sx={{ width: "100%" }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
          </TableBody>
        </Table>

        <Conditional condition={!table.getRowModel().rows?.length && !showSkeleton}>
          <Stack
            sx={{
              height: heightNoData,
              borderTop: (theme) => `1px solid ${theme.palette.table?.border}`,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <Conditional condition={!noDataComponent} elseBlock={noDataComponent}>
              <>
                <img
                  src={imageBoxIconImage}
                  style={{
                    filter: "invert(70%) sepia(6%) saturate(104%) hue-rotate(180deg) brightness(95%) contrast(90%)"
                  }}
                  width={"75px"}
                  alt="No Data"
                />
                <Typography variant="body2" fontWeight={500} color="grey">
                  No Data Found
                </Typography>
              </>
            </Conditional>
          </Stack>
        </Conditional>
      </TableContainer>
      {!hidePagination && (
        <PaginationControls
          hideRowsPerPage={hideRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          table={table}
          justifyContent={paginationJustify}
        />
      )}
    </>
  )
}

/**
 * Helper function to generate sticky column styles
 */
export const getCommonStickyStyles = ({
  row,
  sticky,
  index,
  isScrolledLeft,
  isScrolledRight
}: {
  row: Row<any>
  sticky?: "left" | "right"
  index: number
  isScrolledLeft?: boolean
  isScrolledRight?: boolean
}): SxProps<Theme> => {
  if (!sticky) return {}

  const stickyHeaders = row?.getVisibleCells?.()?.reduce(
    (acc: any, cell, index) => {
      const sticky = cell.column.columnDef?.meta?.sticky
      if (sticky) {
        acc[sticky] = acc[sticky] || []
        acc[sticky].push({ index, width: cell.column.columnDef?.meta?.width })
      }
      return acc
    },
    { left: [], right: [] }
  )

  const stickyOffset = sticky
    ? getStickyColumnOffset(index, sticky === "left" ? stickyHeaders.left : stickyHeaders.right, sticky)
    : "0px"
  const showShadow =
    stickyHeaders.right[0]?.index === index || stickyHeaders.left[stickyHeaders.left.length - 1]?.index === index

  return {
    position: "sticky",
    zIndex: 1,
    [sticky]: stickyOffset,
    background: (theme) => theme.palette.background.default,
    boxShadow: (theme) =>
      isScrolledLeft && sticky === "left" && showShadow
        ? `2px 0 5px -2px ${theme.palette.table?.border}`
        : isScrolledRight && sticky === "right" && showShadow
          ? `-2px 0 5px -2px ${theme.palette.table?.border}`
          : ""
  }
}
